import { create } from 'zustand';
import { persist } from 'zustand/middleware';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;
const WS_URL = process.env.REACT_APP_BACKEND_URL?.replace('https://', 'wss://').replace('http://', 'ws://');
console.log("api", API_URL, WS_URL);
// WebSocket Manager
class WebSocketManager {
  constructor() {
    this.ws = null;
    this.listeners = new Map();
    this.reconnectAttempts = 0;
    this.maxReconnectAttempts = 5;
  }

  connect(token) {
    if (this.ws?.readyState === WebSocket.OPEN) return;
    
    try {
      this.ws = new WebSocket(`${WS_URL}/ws/${token}`);
      
      this.ws.onopen = () => {
        console.log('WebSocket connected');
        this.reconnectAttempts = 0;
        this.startPing();
      };
      
      this.ws.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          this.listeners.forEach((callback) => callback(data));
        } catch (e) {
          console.error('WebSocket message error:', e);
        }
      };
      
      this.ws.onclose = () => {
        console.log('WebSocket disconnected');
        this.stopPing();
        if (this.reconnectAttempts < this.maxReconnectAttempts) {
          this.reconnectAttempts++;
          setTimeout(() => this.connect(token), 2000 * this.reconnectAttempts);
        }
      };
      
      this.ws.onerror = (error) => {
        console.error('WebSocket error:', error);
      };
    } catch (e) {
      console.error('WebSocket connection error:', e);
    }
  }

  disconnect() {
    this.stopPing();
    if (this.ws) {
      this.ws.close();
      this.ws = null;
    }
  }

  startPing() {
    this.pingInterval = setInterval(() => {
      if (this.ws?.readyState === WebSocket.OPEN) {
        this.ws.send(JSON.stringify({ type: 'ping' }));
      }
    }, 30000);
  }

  stopPing() {
    if (this.pingInterval) {
      clearInterval(this.pingInterval);
      this.pingInterval = null;
    }
  }

  subscribe(id, callback) {
    this.listeners.set(id, callback);
    return () => this.listeners.delete(id);
  }
}

export const wsManager = new WebSocketManager();

// Auth Store
export const useAuthStore = create(
  persist(
    (set, get) => ({
      user: null,
      token: null,
      isAuthenticated: false,
      isLoading: false,
      
      setAuth: (user, token) => {
        set({ user, token, isAuthenticated: !!token });
        if (token) {
          wsManager.connect(token);
        }
      },
      
      login: async (email, password) => {
        set({ isLoading: true });
        try {
          const res = await fetch(`${API_URL}/auth/login`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ email, password }),
          });
          
          if (!res.ok) {
            const errorData = await res.json().catch(() => ({}));
            throw new Error(errorData.detail || 'Login failed');
          }
          
          const data = await res.json();
          set({ user: data.user, token: data.token, isAuthenticated: true, isLoading: false });
          wsManager.connect(data.token);
          return data;
        } catch (error) {
          set({ isLoading: false });
          throw error;
        }
      },
      
      register: async (email, password, name, language = 'en') => {
        set({ isLoading: true });
	      console.log("API_URL", API_URL, WS_URL, `${API_URL}/auth/register`);
        try {
          const res = await fetch(`${API_URL}/auth/register`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ email, password, name, language }),
          });
          
          if (!res.ok) {
            const errorData = await res.json().catch(() => ({}));
            throw new Error(errorData.detail || 'Registration failed');
          }
          
          const data = await res.json();
          set({ user: data.user, token: data.token, isAuthenticated: true, isLoading: false });
          wsManager.connect(data.token);
          return data;
        } catch (error) {
          set({ isLoading: false });
          throw error;
        }
      },
      
      logout: () => {
        wsManager.disconnect();
        set({ user: null, token: null, isAuthenticated: false });
      },
      
      updateSettings: async (settings) => {
        const { token } = get();
        try {
          const res = await fetch(`${API_URL}/auth/settings`, {
            method: 'PATCH',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${token}`,
            },
            body: JSON.stringify(settings),
          });
          const data = await res.json();
          if (!res.ok) throw new Error(data.detail || 'Update failed');
          set({ user: { ...get().user, ...data } });
          return data;
        } catch (error) {
          throw error;
        }
      },
      
      getHeaders: () => {
        const { token } = get();
        return {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        };
      },
    }),
    {
      name: 'oslo-auth',
      partialize: (state) => ({ user: state.user, token: state.token, isAuthenticated: state.isAuthenticated }),
      onRehydrateStorage: () => (state) => {
        if (state?.token) {
          wsManager.connect(state.token);
        }
      },
    }
  )
);

// Teams Store
export const useTeamStore = create((set, get) => ({
  teams: [],
  currentTeam: null,
  isLoading: false,
  
  fetchTeams: async () => {
    const headers = useAuthStore.getState().getHeaders();
    set({ isLoading: true });
    try {
      const res = await fetch(`${API_URL}/teams`, { headers });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail);
      set({ teams: data, isLoading: false });
      return data;
    } catch (error) {
      set({ isLoading: false });
      throw error;
    }
  },
  
  createTeam: async (name, description) => {
    const headers = useAuthStore.getState().getHeaders();
    try {
      const res = await fetch(`${API_URL}/teams`, {
        method: 'POST',
        headers,
        body: JSON.stringify({ name, description }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail);
      set((state) => ({ teams: [...state.teams, data] }));
      return data;
    } catch (error) {
      throw error;
    }
  },
  
  inviteMember: async (teamId, email, role) => {
    const headers = useAuthStore.getState().getHeaders();
    try {
      const res = await fetch(`${API_URL}/teams/${teamId}/invite`, {
        method: 'POST',
        headers,
        body: JSON.stringify({ email, role }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail);
      await get().fetchTeams();
      return data;
    } catch (error) {
      throw error;
    }
  },
  
  removeMember: async (teamId, memberId) => {
    const headers = useAuthStore.getState().getHeaders();
    try {
      const res = await fetch(`${API_URL}/teams/${teamId}/members/${memberId}`, {
        method: 'DELETE',
        headers,
      });
      if (!res.ok) {
        const data = await res.json();
        throw new Error(data.detail);
      }
      await get().fetchTeams();
    } catch (error) {
      throw error;
    }
  },
  
  setCurrentTeam: (team) => {
    set({ currentTeam: team });
  },
}));

// Projects Store
export const useProjectStore = create((set, get) => ({
  projects: [],
  currentProject: null,
  isLoading: false,
  
  fetchProjects: async (teamId = null) => {
    const headers = useAuthStore.getState().getHeaders();
    set({ isLoading: true });
    try {
      const url = teamId ? `${API_URL}/projects?team_id=${teamId}` : `${API_URL}/projects`;
      const res = await fetch(url, { headers });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail);
      set({ projects: data, isLoading: false });
      return data;
    } catch (error) {
      set({ isLoading: false });
      throw error;
    }
  },
  
  createProject: async (name, description, teamId = null, isBusinessCritical = false) => {
    const headers = useAuthStore.getState().getHeaders();
    try {
      const res = await fetch(`${API_URL}/projects`, {
        method: 'POST',
        headers,
        body: JSON.stringify({ name, description, team_id: teamId, is_business_critical: isBusinessCritical }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail);
      set((state) => ({ projects: [...state.projects, data] }));
      return data;
    } catch (error) {
      throw error;
    }
  },
  
  setCurrentProject: (project) => {
    set({ currentProject: project });
  },
  
  updateProject: async (projectId, updates) => {
    const headers = useAuthStore.getState().getHeaders();
    try {
      const res = await fetch(`${API_URL}/projects/${projectId}`, {
        method: 'PATCH',
        headers,
        body: JSON.stringify(updates),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail);
      set((state) => ({
        projects: state.projects.map((p) => (p.id === projectId ? data : p)),
        currentProject: state.currentProject?.id === projectId ? data : state.currentProject,
      }));
      return data;
    } catch (error) {
      throw error;
    }
  },
  
  deleteProject: async (projectId) => {
    const headers = useAuthStore.getState().getHeaders();
    try {
      const res = await fetch(`${API_URL}/projects/${projectId}`, {
        method: 'DELETE',
        headers,
      });
      if (!res.ok) {
        const data = await res.json();
        throw new Error(data.detail);
      }
      set((state) => ({
        projects: state.projects.filter((p) => p.id !== projectId),
        currentProject: state.currentProject?.id === projectId ? null : state.currentProject,
      }));
    } catch (error) {
      throw error;
    }
  },
  
  // Project Members Management
  fetchProjectMembers: async (projectId) => {
    const headers = useAuthStore.getState().getHeaders();
    try {
      const res = await fetch(`${API_URL}/projects/${projectId}/members`, { headers });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail);
      return data;
    } catch (error) {
      throw error;
    }
  },
  
  addProjectMember: async (projectId, userId, role) => {
    const headers = useAuthStore.getState().getHeaders();
    try {
      const res = await fetch(`${API_URL}/projects/${projectId}/members`, {
        method: 'POST',
        headers,
        body: JSON.stringify({ user_id: userId, role }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail);
      return data;
    } catch (error) {
      throw error;
    }
  },
  
  removeProjectMember: async (projectId, userId) => {
    const headers = useAuthStore.getState().getHeaders();
    try {
      const res = await fetch(`${API_URL}/projects/${projectId}/members/${userId}`, {
        method: 'DELETE',
        headers,
      });
      if (!res.ok) {
        const data = await res.json();
        throw new Error(data.detail);
      }
    } catch (error) {
      throw error;
    }
  },
  
  updateProjectStatus: async (projectId, status) => {
    const headers = useAuthStore.getState().getHeaders();
    try {
      const res = await fetch(`${API_URL}/projects/${projectId}/status?status=${status}`, {
        method: 'PATCH',
        headers,
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail);
      set((state) => ({
        projects: state.projects.map((p) => (p.id === projectId ? { ...p, status } : p)),
        currentProject: state.currentProject?.id === projectId ? { ...state.currentProject, status } : state.currentProject,
      }));
      return data;
    } catch (error) {
      throw error;
    }
  },
  
  fetchGanttData: async (projectId) => {
    const headers = useAuthStore.getState().getHeaders();
    try {
      const res = await fetch(`${API_URL}/projects/${projectId}/gantt`, { headers });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail);
      return data;
    } catch (error) {
      throw error;
    }
  },
  
  // Handle WebSocket updates
  handleWSUpdate: (message) => {
    const { type, project, project_id } = message;
    switch (type) {
      case 'project_created':
        set((state) => ({
          projects: state.projects.some(p => p.id === project.id) 
            ? state.projects 
            : [...state.projects, project]
        }));
        break;
      case 'project_updated':
        set((state) => ({
          projects: state.projects.map((p) => (p.id === project.id ? project : p)),
          currentProject: state.currentProject?.id === project.id ? project : state.currentProject,
        }));
        break;
      case 'project_deleted':
        set((state) => ({
          projects: state.projects.filter((p) => p.id !== project_id),
          currentProject: state.currentProject?.id === project_id ? null : state.currentProject,
        }));
        break;
      default:
        break;
    }
  },
}));

// Tasks Store
export const useTaskStore = create((set, get) => ({
  tasks: [],
  isLoading: false,
  
  fetchTasks: async (projectId = null) => {
    const headers = useAuthStore.getState().getHeaders();
    set({ isLoading: true });
    try {
      const url = projectId ? `${API_URL}/tasks?project_id=${projectId}` : `${API_URL}/tasks`;
      const res = await fetch(url, { headers });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail);
      set({ tasks: data, isLoading: false });
      return data;
    } catch (error) {
      set({ isLoading: false });
      throw error;
    }
  },
  
  createTask: async (taskData) => {
    const headers = useAuthStore.getState().getHeaders();
    try {
      const res = await fetch(`${API_URL}/tasks`, {
        method: 'POST',
        headers,
        body: JSON.stringify(taskData),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail);
      set((state) => ({ tasks: [...state.tasks, data] }));
      return data;
    } catch (error) {
      throw error;
    }
  },
  
  autoFillTask: async (title, projectId) => {
    const headers = useAuthStore.getState().getHeaders();
    try {
      const res = await fetch(`${API_URL}/tasks/auto-fill`, {
        method: 'POST',
        headers,
        body: JSON.stringify({ title, project_id: projectId }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail);
      return data;
    } catch (error) {
      throw error;
    }
  },
  
  updateTask: async (taskId, updates) => {
    const headers = useAuthStore.getState().getHeaders();
    try {
      const res = await fetch(`${API_URL}/tasks/${taskId}`, {
        method: 'PATCH',
        headers,
        body: JSON.stringify(updates),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail);
      set((state) => ({
        tasks: state.tasks.map((t) => (t.id === taskId ? data : t)),
      }));
      return data;
    } catch (error) {
      throw error;
    }
  },
  
  reorderTask: async (taskId, newStatus, newOrder) => {
    const headers = useAuthStore.getState().getHeaders();
    try {
      const res = await fetch(`${API_URL}/tasks/reorder`, {
        method: 'POST',
        headers,
        body: JSON.stringify({ task_id: taskId, new_status: newStatus, new_order: newOrder }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail);
      // Refetch tasks to get updated order
      await get().fetchTasks(data.project_id);
      return data;
    } catch (error) {
      throw error;
    }
  },
  
  addSubtask: async (taskId, title) => {
    const headers = useAuthStore.getState().getHeaders();
    try {
      const res = await fetch(`${API_URL}/tasks/${taskId}/subtasks`, {
        method: 'POST',
        headers,
        body: JSON.stringify({ title, completed: false }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail);
      set((state) => ({
        tasks: state.tasks.map((t) => (t.id === taskId ? data : t)),
      }));
      return data;
    } catch (error) {
      throw error;
    }
  },
  
  updateSubtask: async (taskId, subtaskId, updates) => {
    const headers = useAuthStore.getState().getHeaders();
    try {
      const res = await fetch(`${API_URL}/tasks/${taskId}/subtasks/${subtaskId}`, {
        method: 'PATCH',
        headers,
        body: JSON.stringify(updates),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail);
      set((state) => ({
        tasks: state.tasks.map((t) => (t.id === taskId ? data : t)),
      }));
      return data;
    } catch (error) {
      throw error;
    }
  },
  
  deleteSubtask: async (taskId, subtaskId) => {
    const headers = useAuthStore.getState().getHeaders();
    try {
      const res = await fetch(`${API_URL}/tasks/${taskId}/subtasks/${subtaskId}`, {
        method: 'DELETE',
        headers,
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail);
      set((state) => ({
        tasks: state.tasks.map((t) => (t.id === taskId ? data : t)),
      }));
      return data;
    } catch (error) {
      throw error;
    }
  },
  
  deleteTask: async (taskId) => {
    const headers = useAuthStore.getState().getHeaders();
    try {
      const res = await fetch(`${API_URL}/tasks/${taskId}`, {
        method: 'DELETE',
        headers,
      });
      if (!res.ok) {
        const data = await res.json();
        throw new Error(data.detail);
      }
      set((state) => ({
        tasks: state.tasks.filter((t) => t.id !== taskId),
      }));
    } catch (error) {
      throw error;
    }
  },
  
  // Optimistic update for drag and drop
  optimisticReorder: (taskId, newStatus, newOrder) => {
    set((state) => ({
      tasks: state.tasks.map((t) => 
        t.id === taskId ? { ...t, status: newStatus, order: newOrder } : t
      ).sort((a, b) => a.order - b.order),
    }));
  },
  
  // Comments
  fetchComments: async (taskId) => {
    const headers = useAuthStore.getState().getHeaders();
    try {
      const res = await fetch(`${API_URL}/tasks/${taskId}/comments`, { headers });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail);
      return data;
    } catch (error) {
      throw error;
    }
  },
  
  addComment: async (taskId, content) => {
    const headers = useAuthStore.getState().getHeaders();
    try {
      const res = await fetch(`${API_URL}/tasks/${taskId}/comments`, {
        method: 'POST',
        headers,
        body: JSON.stringify({ task_id: taskId, content }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail);
      return data;
    } catch (error) {
      throw error;
    }
  },
  
  deleteComment: async (commentId) => {
    const headers = useAuthStore.getState().getHeaders();
    try {
      const res = await fetch(`${API_URL}/comments/${commentId}`, {
        method: 'DELETE',
        headers,
      });
      if (!res.ok) {
        const data = await res.json();
        throw new Error(data.detail);
      }
    } catch (error) {
      throw error;
    }
  },
  
  // Activity
  fetchActivity: async (taskId) => {
    const headers = useAuthStore.getState().getHeaders();
    try {
      const res = await fetch(`${API_URL}/tasks/${taskId}/activity`, { headers });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail);
      return data;
    } catch (error) {
      throw error;
    }
  },
  
  // Attachments
  fetchAttachments: async (taskId) => {
    const headers = useAuthStore.getState().getHeaders();
    try {
      const res = await fetch(`${API_URL}/tasks/${taskId}/attachments`, { headers });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail);
      return data;
    } catch (error) {
      throw error;
    }
  },
  
  uploadAttachment: async (taskId, file) => {
    const token = useAuthStore.getState().token;
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      const res = await fetch(`${API_URL}/tasks/${taskId}/attachments`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formData,
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail);
      return data;
    } catch (error) {
      throw error;
    }
  },
  
  deleteAttachment: async (attachmentId) => {
    const headers = useAuthStore.getState().getHeaders();
    try {
      const res = await fetch(`${API_URL}/attachments/${attachmentId}`, {
        method: 'DELETE',
        headers,
      });
      if (!res.ok) {
        const data = await res.json();
        throw new Error(data.detail);
      }
    } catch (error) {
      throw error;
    }
  },
  
  // Time Tracking
  fetchTimeEntries: async (taskId) => {
    const headers = useAuthStore.getState().getHeaders();
    try {
      const res = await fetch(`${API_URL}/tasks/${taskId}/time-entries`, { headers });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail);
      return data;
    } catch (error) {
      throw error;
    }
  },
  
  logTime: async (taskId, hours, description = '', date = null) => {
    const headers = useAuthStore.getState().getHeaders();
    try {
      const res = await fetch(`${API_URL}/tasks/${taskId}/time-entries`, {
        method: 'POST',
        headers,
        body: JSON.stringify({ task_id: taskId, hours, description, date }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail);
      return data;
    } catch (error) {
      throw error;
    }
  },
  
  deleteTimeEntry: async (entryId) => {
    const headers = useAuthStore.getState().getHeaders();
    try {
      const res = await fetch(`${API_URL}/time-entries/${entryId}`, {
        method: 'DELETE',
        headers,
      });
      if (!res.ok) {
        const data = await res.json();
        throw new Error(data.detail);
      }
    } catch (error) {
      throw error;
    }
  },
  
  getUserTimeSummary: async (userId, startDate = null, endDate = null) => {
    const headers = useAuthStore.getState().getHeaders();
    try {
      let url = `${API_URL}/users/${userId}/time-summary`;
      const params = new URLSearchParams();
      if (startDate) params.append('start_date', startDate);
      if (endDate) params.append('end_date', endDate);
      if (params.toString()) url += `?${params.toString()}`;
      
      const res = await fetch(url, { headers });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail);
      return data;
    } catch (error) {
      throw error;
    }
  },
  
  // Recurring Tasks
  createNextOccurrence: async (taskId) => {
    const headers = useAuthStore.getState().getHeaders();
    try {
      const res = await fetch(`${API_URL}/tasks/${taskId}/create-next-occurrence`, {
        method: 'POST',
        headers,
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail);
      set((state) => ({ tasks: [...state.tasks, data] }));
      return data;
    } catch (error) {
      throw error;
    }
  },
  
  // Handle WebSocket updates
  handleWSUpdate: (message) => {
    const { type, task, task_id } = message;
    switch (type) {
      case 'task_created':
        set((state) => ({
          tasks: state.tasks.some(t => t.id === task.id) 
            ? state.tasks 
            : [...state.tasks, task]
        }));
        break;
      case 'task_updated':
      case 'task_reordered':
        set((state) => ({
          tasks: state.tasks.map((t) => (t.id === task.id ? task : t)),
        }));
        break;
      case 'task_deleted':
        set((state) => ({
          tasks: state.tasks.filter((t) => t.id !== task_id),
        }));
        break;
      default:
        break;
    }
  },
}));

// Chat Store
export const useChatStore = create((set, get) => ({
  messages: [],
  isLoading: false,
  
  fetchHistory: async (projectId = null) => {
    const headers = useAuthStore.getState().getHeaders();
    try {
      const url = projectId ? `${API_URL}/chat/history?project_id=${projectId}` : `${API_URL}/chat/history`;
      const res = await fetch(url, { headers });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail);
      set({ messages: data });
      return data;
    } catch (error) {
      throw error;
    }
  },
  
  sendMessage: async (message, projectId = null) => {
    const headers = useAuthStore.getState().getHeaders();
    set({ isLoading: true });
    
    const userMsg = {
      id: `temp-${Date.now()}`,
      role: 'user',
      content: message,
      created_at: new Date().toISOString(),
    };
    set((state) => ({ messages: [...state.messages, userMsg] }));
    
    try {
      const res = await fetch(`${API_URL}/chat`, {
        method: 'POST',
        headers,
        body: JSON.stringify({ message, project_id: projectId }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail);
      
      const aiMsg = {
        id: data.id,
        role: 'assistant',
        content: data.response,
        created_at: data.created_at,
      };
      set((state) => ({ 
        messages: [...state.messages, aiMsg],
        isLoading: false 
      }));
      return data;
    } catch (error) {
      set({ isLoading: false });
      throw error;
    }
  },
  
  clearMessages: () => {
    set({ messages: [] });
  },
}));

// Agents Store
export const useAgentStore = create((set, get) => ({
  agents: [],
  isLoading: false,
  
  fetchAgents: async () => {
    const headers = useAuthStore.getState().getHeaders();
    set({ isLoading: true });
    try {
      const res = await fetch(`${API_URL}/agents`, { headers });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail);
      set({ agents: data, isLoading: false });
      return data;
    } catch (error) {
      set({ isLoading: false });
      throw error;
    }
  },
  
  createAgent: async (agentData) => {
    const headers = useAuthStore.getState().getHeaders();
    try {
      const res = await fetch(`${API_URL}/agents`, {
        method: 'POST',
        headers,
        body: JSON.stringify(agentData),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail);
      set((state) => ({ agents: [...state.agents, data] }));
      return data;
    } catch (error) {
      throw error;
    }
  },
  
  updateAgent: async (agentId, updates) => {
    const headers = useAuthStore.getState().getHeaders();
    try {
      const res = await fetch(`${API_URL}/agents/${agentId}`, {
        method: 'PATCH',
        headers,
        body: JSON.stringify(updates),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail);
      set((state) => ({
        agents: state.agents.map((a) => (a.id === agentId ? data : a)),
      }));
      return data;
    } catch (error) {
      throw error;
    }
  },
  
  deleteAgent: async (agentId) => {
    const headers = useAuthStore.getState().getHeaders();
    try {
      const res = await fetch(`${API_URL}/agents/${agentId}`, {
        method: 'DELETE',
        headers,
      });
      if (!res.ok) {
        const data = await res.json();
        throw new Error(data.detail);
      }
      set((state) => ({
        agents: state.agents.filter((a) => a.id !== agentId),
      }));
    } catch (error) {
      throw error;
    }
  },
}));

// Reports Store
export const useReportStore = create((set, get) => ({
  summary: null,
  insights: [],
  topPerformers: [],
  isLoading: false,
  
  fetchSummary: async () => {
    const headers = useAuthStore.getState().getHeaders();
    set({ isLoading: true });
    try {
      const res = await fetch(`${API_URL}/reports/summary`, { headers });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail);
      set({ summary: data, isLoading: false });
      return data;
    } catch (error) {
      set({ isLoading: false });
      throw error;
    }
  },
  
  fetchInsights: async () => {
    const headers = useAuthStore.getState().getHeaders();
    try {
      const res = await fetch(`${API_URL}/reports/ai-insights`, { headers });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail);
      set({ insights: data.insights || [] });
      return data;
    } catch (error) {
      throw error;
    }
  },
  
  fetchTopPerformers: async (teamId = null) => {
    const headers = useAuthStore.getState().getHeaders();
    try {
      const url = teamId 
        ? `${API_URL}/reports/top-performers?team_id=${teamId}` 
        : `${API_URL}/reports/top-performers`;
      const res = await fetch(url, { headers });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail);
      set({ topPerformers: data.top_performers || [] });
      return data;
    } catch (error) {
      throw error;
    }
  },
}));

// Department Store
export const useDepartmentStore = create((set, get) => ({
  departments: [],
  currentDepartment: null,
  pipeline: null,
  userTasks: null,
  isLoading: false,
  
  fetchDepartments: async (teamId = null) => {
    const headers = useAuthStore.getState().getHeaders();
    set({ isLoading: true });
    try {
      const url = teamId ? `${API_URL}/departments?team_id=${teamId}` : `${API_URL}/departments`;
      const res = await fetch(url, { headers });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail);
      set({ departments: data, isLoading: false });
      return data;
    } catch (error) {
      set({ isLoading: false });
      throw error;
    }
  },
  
  createDepartment: async (name, description, teamId) => {
    const headers = useAuthStore.getState().getHeaders();
    try {
      const res = await fetch(`${API_URL}/departments`, {
        method: 'POST',
        headers,
        body: JSON.stringify({ name, description, team_id: teamId }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail);
      set((state) => ({ departments: [...state.departments, data] }));
      return data;
    } catch (error) {
      throw error;
    }
  },
  
  deleteDepartment: async (deptId) => {
    const headers = useAuthStore.getState().getHeaders();
    try {
      const res = await fetch(`${API_URL}/departments/${deptId}`, {
        method: 'DELETE',
        headers,
      });
      if (!res.ok) {
        const data = await res.json();
        throw new Error(data.detail);
      }
      set((state) => ({
        departments: state.departments.filter((d) => d.id !== deptId),
      }));
    } catch (error) {
      throw error;
    }
  },
  
  addMemberToDepartment: async (deptId, userId) => {
    const headers = useAuthStore.getState().getHeaders();
    try {
      const res = await fetch(`${API_URL}/departments/${deptId}/members`, {
        method: 'POST',
        headers,
        body: JSON.stringify({ user_id: userId, department_id: deptId }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail);
      await get().fetchDepartments();
      return data;
    } catch (error) {
      throw error;
    }
  },
  
  removeMemberFromDepartment: async (deptId, userId) => {
    const headers = useAuthStore.getState().getHeaders();
    try {
      const res = await fetch(`${API_URL}/departments/${deptId}/members/${userId}`, {
        method: 'DELETE',
        headers,
      });
      if (!res.ok) {
        const data = await res.json();
        throw new Error(data.detail);
      }
      await get().fetchDepartments();
    } catch (error) {
      throw error;
    }
  },
  
  fetchPipeline: async (deptId) => {
    const headers = useAuthStore.getState().getHeaders();
    try {
      const res = await fetch(`${API_URL}/departments/${deptId}/pipeline`, { headers });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail);
      set({ pipeline: data, currentDepartment: data.department });
      return data;
    } catch (error) {
      throw error;
    }
  },
  
  fetchUserTasks: async (userId) => {
    const headers = useAuthStore.getState().getHeaders();
    try {
      const res = await fetch(`${API_URL}/users/${userId}/tasks`, { headers });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail);
      set({ userTasks: data });
      return data;
    } catch (error) {
      throw error;
    }
  },
  
  setCurrentDepartment: (dept) => set({ currentDepartment: dept }),
  clearPipeline: () => set({ pipeline: null, userTasks: null }),
}));

// Admin Store
export const useAdminStore = create((set, get) => ({
  users: [],
  settings: null,
  approvals: [],
  isLoading: false,
  
  fetchUsers: async () => {
    const { token } = useAuthStore.getState();
    set({ isLoading: true });
    try {
      const res = await fetch(`${API_URL}/admin/users`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Failed to fetch users');
      set({ users: data, isLoading: false });
      return data;
    } catch (error) {
      set({ isLoading: false });
      throw error;
    }
  },
  
  createUser: async (userData) => {
    const { token } = useAuthStore.getState();
    const res = await fetch(`${API_URL}/admin/users`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
      },
      body: JSON.stringify(userData),
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data.detail || 'Failed to create user');
    set({ users: [...get().users, data] });
    return data;
  },
  
  updateUser: async (userId, updates) => {
    const { token } = useAuthStore.getState();
    const res = await fetch(`${API_URL}/admin/users/${userId}`, {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
      },
      body: JSON.stringify(updates),
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data.detail || 'Failed to update user');
    set({ users: get().users.map(u => u.id === userId ? data : u) });
    return data;
  },
  
  deleteUser: async (userId) => {
    const { token } = useAuthStore.getState();
    const res = await fetch(`${API_URL}/admin/users/${userId}`, {
      method: 'DELETE',
      headers: { 'Authorization': `Bearer ${token}` },
    });
    if (!res.ok) {
      const data = await res.json();
      throw new Error(data.detail || 'Failed to delete user');
    }
    set({ users: get().users.filter(u => u.id !== userId) });
  },
  
  fetchSettings: async () => {
    const { token } = useAuthStore.getState();
    try {
      const res = await fetch(`${API_URL}/admin/settings`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Failed to fetch settings');
      set({ settings: data });
      return data;
    } catch (error) {
      throw error;
    }
  },
  
  updateSettings: async (updates) => {
    const { token } = useAuthStore.getState();
    const res = await fetch(`${API_URL}/admin/settings`, {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
      },
      body: JSON.stringify(updates),
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data.detail || 'Failed to update settings');
    set({ settings: data });
    return data;
  },
  
  fetchApprovals: async (status = null) => {
    const { token } = useAuthStore.getState();
    try {
      const url = status ? `${API_URL}/approvals?status=${status}` : `${API_URL}/approvals`;
      const res = await fetch(url, {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Failed to fetch approvals');
      set({ approvals: data });
      return data;
    } catch (error) {
      throw error;
    }
  },
  
  approveRequest: async (approvalId) => {
    const { token } = useAuthStore.getState();
    const res = await fetch(`${API_URL}/approvals/${approvalId}/approve`, {
      method: 'POST',
      headers: { 'Authorization': `Bearer ${token}` },
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data.detail || 'Failed to approve');
    set({ approvals: get().approvals.filter(a => a.id !== approvalId) });
    return data;
  },
  
  rejectRequest: async (approvalId, reason) => {
    const { token } = useAuthStore.getState();
    const res = await fetch(`${API_URL}/approvals/${approvalId}/reject?reason=${encodeURIComponent(reason || '')}`, {
      method: 'POST',
      headers: { 'Authorization': `Bearer ${token}` },
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data.detail || 'Failed to reject');
    set({ approvals: get().approvals.filter(a => a.id !== approvalId) });
    return data;
  },
}));

// UI Store
export const useUIStore = create(
  persist(
    (set) => ({
      language: 'en',
      mode: 'simple',
      sidebarOpen: true,
      voiceOverlayOpen: false,
      
      setLanguage: (language) => set({ language }),
      setMode: (mode) => set({ mode }),
      toggleSidebar: () => set((state) => ({ sidebarOpen: !state.sidebarOpen })),
      setVoiceOverlayOpen: (open) => set({ voiceOverlayOpen: open }),
    }),
    {
      name: 'oslo-ui',
    }
  )
);

// Initialize WebSocket listeners
if (typeof window !== 'undefined') {
  wsManager.subscribe('projects', (message) => {
    if (['project_created', 'project_updated', 'project_deleted'].includes(message.type)) {
      useProjectStore.getState().handleWSUpdate(message);
    }
  });
  
  wsManager.subscribe('tasks', (message) => {
    if (['task_created', 'task_updated', 'task_deleted', 'task_reordered'].includes(message.type)) {
      useTaskStore.getState().handleWSUpdate(message);
    }
  });
}
